/**
 * useMyAccountant hook.
 *
 * The single source of truth for the business owner's accountant
 * relationship. One accountant per business, one record on disk, plus
 * a list of previously-connected accountants kept for Re-invite.
 *
 * Phase A (now): backed by localStorage under
 *   "novala.myAccountant.record"   (active record, singular)
 *   "novala.myAccountant.previous" (history list)
 * All mutations read/write those keys. When the Phase B backend lands,
 * swap the localStorage calls for API calls inside this file; no page
 * or component changes.
 *
 * Return shape follows the Novala data-architecture rule:
 * { data, previousAccountants, loading, error, ...mutations, refetch }.
 * TanStack Query compatible.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  MyAccountant,
  InviteDraft,
  PreviousAccountant,
  PreviousAccountantReason,
} from "../pages/Accounting/MyAccountant/myAccountantTypes";
import { computeExpiryDate } from "../pages/Accounting/MyAccountant/myAccountantUtils";
import {
  STORAGE_KEY_PREVIOUS,
  EXPIRED_ARCHIVE_AFTER_DAYS,
} from "../pages/Accounting/MyAccountant/constants";

const STORAGE_KEY_ACTIVE = "novala.myAccountant.record";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Safe localStorage read for the active record. */
function readActive(): MyAccountant | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACTIVE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (
      typeof parsed.id !== "string" ||
      typeof parsed.status !== "string" ||
      typeof parsed.email !== "string" ||
      typeof parsed.expiresAt !== "string"
    ) {
      return null;
    }
    return parsed as MyAccountant;
  } catch {
    return null;
  }
}

/** Safe localStorage write for the active record. */
function writeActive(record: MyAccountant | null): void {
  try {
    if (record === null) {
      localStorage.removeItem(STORAGE_KEY_ACTIVE);
    } else {
      localStorage.setItem(STORAGE_KEY_ACTIVE, JSON.stringify(record));
    }
  } catch {
    // silent
  }
}

/** Safe localStorage read for the previously-connected list. */
function readPrevious(): PreviousAccountant[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREVIOUS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is PreviousAccountant =>
        p &&
        typeof p.id === "string" &&
        typeof p.email === "string" &&
        typeof p.reason === "string" &&
        typeof p.archivedAt === "string",
    );
  } catch {
    return [];
  }
}

/** Safe localStorage write for the previously-connected list. */
function writePrevious(list: PreviousAccountant[]): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREVIOUS, JSON.stringify(list));
  } catch {
    // silent
  }
}

/** Generate a stable-ish id for a new record. Phase B will use server uuids. */
function generateId(): string {
  return `acct_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

/** Build a PreviousAccountant entry from the active record being archived. */
function toPrevious(
  record: MyAccountant,
  reason: PreviousAccountantReason,
): PreviousAccountant {
  return {
    id: record.id,
    email: record.email,
    name: record.name,
    firmName: record.firmName,
    reason,
    archivedAt: new Date().toISOString(),
  };
}

/** Returns true when a pending record is past its expiresAt. */
function isPastExpiry(record: MyAccountant): boolean {
  return new Date(record.expiresAt).getTime() < Date.now();
}

/** Returns true when an expired record is older than the archive window and should auto-move to history. */
function shouldAutoArchive(record: MyAccountant): boolean {
  if (record.status !== "expired") return false;
  const expiryMs = new Date(record.expiresAt).getTime();
  const archiveMs = expiryMs + EXPIRED_ARCHIVE_AFTER_DAYS * MS_PER_DAY;
  return Date.now() > archiveMs;
}

export interface UseMyAccountantResult {
  /** Current accountant record, or null when nothing is invited yet. */
  data: MyAccountant | null;
  /** Past accountants kept for Re-invite, newest archive first. */
  previousAccountants: PreviousAccountant[];
  /** Loading flag, always false in Phase A. */
  loading: boolean;
  /** Last error, if any. */
  error: Error | null;

  /** Send a new invite. Overwrites any previous record. Returns the new record. */
  invite: (draft: InviteDraft) => MyAccountant;
  /** Re-invite someone from history. Removes them from history and writes a fresh pending record. */
  reinvite: (previousId: string, draft: InviteDraft) => MyAccountant;
  /** Reset the invite timer by extending expiresAt by 30 days from now. */
  resend: (id: string) => void;
  /** Cancel a pending invite. Clears the record. Does NOT add to history. */
  cancel: (id: string) => void;
  /** Remove an active accountant. Clears the record and adds to Previously connected. */
  remove: (id: string) => void;
  /** Dismiss an expired invite. Clears the record and adds to Previously connected. */
  dismissExpired: (id: string) => void;
  /** Remove an entry from Previously connected. */
  forgetPrevious: (previousId: string) => void;

  /** Re-read from storage. Useful after external changes. */
  refetch: () => void;
  /** Dev-only: write any record directly for state testing. */
  setForDev: (record: MyAccountant | null) => void;
}

export function useMyAccountant(): UseMyAccountantResult {
  const [data, setData] = useState<MyAccountant | null>(() => readActive());
  const [previousAccountants, setPreviousAccountants] = useState<
    PreviousAccountant[]
  >(() => readPrevious());
  const [error] = useState<Error | null>(null);
  const loading = false;

  /** Core sync: read storage, auto-flip pending→expired, auto-archive stale expired. */
  const refetch = useCallback(() => {
    const current = readActive();
    if (!current) {
      setData(null);
      setPreviousAccountants(readPrevious());
      return;
    }

    // Auto-archive expired records that have sat too long.
    if (shouldAutoArchive(current)) {
      const next = [toPrevious(current, "expired"), ...readPrevious()];
      writePrevious(next);
      writeActive(null);
      setData(null);
      setPreviousAccountants(next);
      return;
    }

    // Auto-flip pending → expired when the clock runs out.
    if (current.status === "pending" && isPastExpiry(current)) {
      const expired: MyAccountant = { ...current, status: "expired" };
      writeActive(expired);
      setData(expired);
      setPreviousAccountants(readPrevious());
      return;
    }

    setData(current);
    setPreviousAccountants(readPrevious());
  }, []);

  // Re-check on mount and whenever the window regains focus (fast-forward testing).
  useEffect(() => {
    refetch();
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetch]);

  // Keep in sync with other tabs.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY_ACTIVE || e.key === STORAGE_KEY_PREVIOUS) {
        refetch();
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refetch]);

  const invite = useCallback((draft: InviteDraft): MyAccountant => {
    const now = new Date();
    const record: MyAccountant = {
      id: generateId(),
      status: "pending",
      email: draft.email.trim(),
      name: draft.name.trim() || undefined,
      firmName: draft.firmName.trim() || undefined,
      role: "accountant",
      invitedAt: now.toISOString(),
      expiresAt: computeExpiryDate(now),
    };
    writeActive(record);
    setData(record);
    return record;
  }, []);

  const reinvite = useCallback(
    (previousId: string, draft: InviteDraft): MyAccountant => {
      // Create the fresh invite first.
      const now = new Date();
      const record: MyAccountant = {
        id: generateId(),
        status: "pending",
        email: draft.email.trim(),
        name: draft.name.trim() || undefined,
        firmName: draft.firmName.trim() || undefined,
        role: "accountant",
        invitedAt: now.toISOString(),
        expiresAt: computeExpiryDate(now),
      };
      writeActive(record);
      setData(record);
      // Then clean the matching entry out of history (if still there).
      const nextPrev = readPrevious().filter((p) => p.id !== previousId);
      writePrevious(nextPrev);
      setPreviousAccountants(nextPrev);
      return record;
    },
    [],
  );

  const resend = useCallback((id: string) => {
    const current = readActive();
    if (!current || current.id !== id) return;
    const now = new Date();
    const updated: MyAccountant = {
      ...current,
      invitedAt: now.toISOString(),
      expiresAt: computeExpiryDate(now),
      status: "pending",
    };
    writeActive(updated);
    setData(updated);
  }, []);

  const cancel = useCallback((id: string) => {
    const current = readActive();
    if (!current || current.id !== id) return;
    writeActive(null);
    setData(null);
  }, []);

  const remove = useCallback((id: string) => {
    const current = readActive();
    if (!current || current.id !== id) return;
    const nextPrev = [toPrevious(current, "removed"), ...readPrevious()];
    writePrevious(nextPrev);
    writeActive(null);
    setData(null);
    setPreviousAccountants(nextPrev);
  }, []);

  const dismissExpired = useCallback((id: string) => {
    const current = readActive();
    if (!current || current.id !== id) return;
    const nextPrev = [toPrevious(current, "expired"), ...readPrevious()];
    writePrevious(nextPrev);
    writeActive(null);
    setData(null);
    setPreviousAccountants(nextPrev);
  }, []);

  const forgetPrevious = useCallback((previousId: string) => {
    const nextPrev = readPrevious().filter((p) => p.id !== previousId);
    writePrevious(nextPrev);
    setPreviousAccountants(nextPrev);
  }, []);

  const setForDev = useCallback((record: MyAccountant | null) => {
    writeActive(record);
    setData(record);
  }, []);

  return {
    data,
    previousAccountants,
    loading,
    error,
    invite,
    reinvite,
    resend,
    cancel,
    remove,
    dismissExpired,
    forgetPrevious,
    refetch,
    setForDev,
  };
}